import React, { useMemo } from "react";
import { Keg as DDMKeg, KegStyles } from "@ddm-design-system/keg";
import styled, { css } from "styled-components";
import { IX10 } from "../../../store/installation/types";
import { generateInitials } from "../../../helpers";
import { X10_KEG_VOLUME_LITRES } from "../../../constants";

const StyledX10Keg = styled(DDMKeg)<{ dimmed: boolean; open: boolean; danger: boolean }>(
  ({ theme, dimmed, open, danger }) => css`
    background-color: ${theme.colors.white};

    ${danger &&
    css`
      border-color: ${theme.colors.alert.alert100} !important;
    `}

    ${dimmed &&
    css`
      opacity: 0.5;
      border-color: ${theme.colors.grey.grey50} !important;
    `}

    ${open &&
    css`
      ${KegStyles.StyledKegLabel} {
        opacity: 0;
      }

      ${KegStyles.StyledBeverage} {
        opacity: 0;
      }
    `}
  `
);

interface IProps {
  x10: IX10;
  className?: string;
}

/** The design-system keg sized for a 10 L X10 keg. */
export const X10Keg: React.FC<IProps> = ({ x10, className }) => {
  const logoFallback = useMemo(
    () => ({
      color: (x10.beverageKnown && x10.beverage.mainColor) || "",
      initials: x10.beverageKnown
        ? generateInitials(x10.beverage.brand, x10.beverage.subbrand)
        : "?"
    }),
    [x10.beverage, x10.beverageKnown]
  );

  return (
    <StyledX10Keg
      className={className}
      emptyLabel="0L"
      // the last known volume of an offline machine is stale, so it is hidden like a missing keg
      hasError={x10.offline || !x10.hasKeg}
      hasWarning={!x10.offline && x10.status === "warning"}
      danger={!x10.offline && x10.status === "danger"}
      logoFallback={logoFallback}
      logoUrl={(x10.beverageKnown && x10.beverage.logoUrl) || undefined}
      maxVolume={x10.volume || X10_KEG_VOLUME_LITRES}
      volumeLeft={x10.volumeLeft}
      decimalCases={1}
      warningThresholdPct={10}
      dimmed={x10.offline || x10.open}
      open={x10.open && !x10.offline}
    />
  );
};

export default X10Keg;
